// SavedViewChips — user-pinned filter presets for pipeline pages.
//
// Sales users kept rebuilding the same "Gold · Rotting" filter tens of times
// per day. This component gives them a one-click chip row above the filter
// bar — save the current filter state as a named view, tap the chip to
// re-apply it. Everything is localStorage-backed so it survives across
// sessions on the same device without needing a backend user_prefs table.
//
// Usage:
//   const {views, saveCurrent, apply, remove} =
//     useSavedViews("deals.savedViews", currentSnapshot);
//   <SavedViewChips
//     views={views} onSave={saveCurrent}
//     onApply={apply} onDelete={remove}
//     testidPrefix="deal-view"
//   />
//
// `currentSnapshot` is whatever object shape you want to persist — the
// component is agnostic (it just serialises + restores it).
import { useCallback, useEffect, useState } from "react";
import { Bookmark, Plus, X, Check } from "lucide-react";

const _readLS = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const _writeLS = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* quota */ }
};

const _uid = () => `sv_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function useSavedViews(storageKey, currentSnapshot) {
  const [views, setViews] = useState(() => _readLS(storageKey));

  // Sync across tabs — cheap belt & braces.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === storageKey) setViews(_readLS(storageKey));
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [storageKey]);

  const persist = useCallback((next) => {
    setViews(next);
    _writeLS(storageKey, next);
  }, [storageKey]);

  const saveCurrent = useCallback((name) => {
    const clean = (name || "").trim().slice(0, 40);
    if (!clean) return;
    const next = [
      ...views.filter((v) => v.name !== clean),   // dedupe by name
      { id: _uid(), name: clean, snapshot: currentSnapshot },
    ];
    persist(next);
  }, [views, currentSnapshot, persist]);

  const remove = useCallback((id) => {
    persist(views.filter((v) => v.id !== id));
  }, [views, persist]);

  return { views, saveCurrent, remove };
}

/** Compare two snapshots for shallow equality — arrays are joined+compared,
 *  strings are trimmed. Used to highlight the currently-active view chip. */
function _snapshotsMatch(a, b) {
  if (!a || !b) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    const va = a[k], vb = b[k];
    if (Array.isArray(va) && Array.isArray(vb)) {
      if ([...va].sort().join("|") !== [...vb].sort().join("|")) return false;
    } else if ((va ?? "") !== (vb ?? "")) {
      return false;
    }
  }
  return true;
}

export default function SavedViewChips({
  views, onApply, onDelete, onSave,
  currentSnapshot, testidPrefix = "saved-view",
  className = "",
}) {
  const [naming, setNaming] = useState(false);
  const [draftName, setDraftName] = useState("");

  const commit = useCallback(() => {
    onSave?.(draftName);
    setDraftName("");
    setNaming(false);
  }, [draftName, onSave]);

  if (!views || (views.length === 0 && !naming)) {
    // Empty state — just a "Save current view…" prompt (keeps the row visible so
    // sales knows the feature exists).
    return (
      <div className={`flex items-center gap-2 ${className}`} data-testid={`${testidPrefix}-row`}>
        <Bookmark className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500">
          Saved views
        </span>
        <button
          type="button"
          onClick={() => setNaming(true)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-dashed border-white/10 text-[11px] text-slate-400 hover:text-white hover:border-white/30"
          data-testid={`${testidPrefix}-add-empty`}
        >
          <Plus className="w-3 h-3" /> Save current filters
        </button>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1.5 flex-wrap ${className}`} data-testid={`${testidPrefix}-row`}>
      <Bookmark className="w-3.5 h-3.5 text-slate-500 shrink-0" />
      {views.map((v) => {
        const active = _snapshotsMatch(currentSnapshot, v.snapshot);
        return (
          <span
            key={v.id}
            className={`group inline-flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 border text-[11px] cursor-pointer transition-colors ${
              active
                ? "border-amber-400/60 bg-amber-400/[0.08] text-amber-200"
                : "border-white/10 bg-white/[0.04] text-slate-300 hover:border-white/30 hover:text-white"
            }`}
            data-testid={`${testidPrefix}-chip-${v.id}`}
            title={active ? "Currently applied" : "Click to apply this filter set"}
          >
            <button
              type="button"
              onClick={() => onApply?.(v.snapshot)}
              className="max-w-[160px] truncate"
              data-testid={`${testidPrefix}-apply-${v.id}`}
            >
              {v.name}
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete?.(v.id); }}
              className="opacity-40 group-hover:opacity-100 hover:text-rose-300 rounded-full w-4 h-4 grid place-items-center"
              data-testid={`${testidPrefix}-delete-${v.id}`}
              aria-label={`Delete saved view ${v.name}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        );
      })}
      {naming ? (
        <form
          onSubmit={(e) => { e.preventDefault(); commit(); }}
          className="inline-flex items-center gap-1 rounded-full border border-white/20 bg-white/[0.04] pl-2 pr-0.5 py-0.5"
        >
          <input
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") { setNaming(false); setDraftName(""); } }}
            placeholder="Name this view…"
            maxLength={40}
            className="w-40 bg-transparent text-[11px] text-white placeholder:text-slate-500 focus:outline-none"
            data-testid={`${testidPrefix}-name-input`}
          />
          <button
            type="submit"
            disabled={!draftName.trim()}
            className="rounded-full w-5 h-5 grid place-items-center bg-teal-400/25 text-teal-200 hover:bg-teal-400/40 disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid={`${testidPrefix}-name-confirm`}
            aria-label="Save"
          >
            <Check className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => { setNaming(false); setDraftName(""); }}
            className="rounded-full w-5 h-5 grid place-items-center text-slate-400 hover:text-white"
            aria-label="Cancel"
          >
            <X className="w-3 h-3" />
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setNaming(true)}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-dashed border-white/10 text-[11px] text-slate-500 hover:text-white hover:border-white/30"
          data-testid={`${testidPrefix}-add`}
          title="Save current filters as a new view"
        >
          <Plus className="w-3 h-3" /> Save view
        </button>
      )}
    </div>
  );
}
